import React, { useState, useCallback } from "react";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import Alert from "@mui/material/Alert";
import IconButton from "@mui/material/IconButton";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import CircularProgress from "@mui/material/CircularProgress";
import InputAdornment from "@mui/material/InputAdornment";
import Link from "@mui/material/Link";
import { alpha } from "@mui/material/styles";

import SaveIcon from "@mui/icons-material/Save";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import SearchIcon from "@mui/icons-material/Search";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";

import { searchCep } from "../../../extras";
import { UF_OPTIONS, HOW_HEARD_OPTIONS, EMPTY_RESTAURANT, LGPD_TEXT } from "../constants";

export default function OptionalDetailsForm({
  detailsData,
  errors,
  submitting,
  submitError,
  updateDetailsField,
  onSubmit,
  onSkip,
}) {
  const restaurants = detailsData.restaurants || [];
  const restaurantErrors = errors.restaurantErrors || [];

  const [cepLoading, setCepLoading] = useState(false);
  const [cepError, setCepError] = useState("");

  // --- Restaurant helpers ---
  const updateRestaurant = useCallback(
    (index, field, value) => {
      const updated = restaurants.map((r, i) =>
        i === index ? { ...r, [field]: value } : r
      );
      updateDetailsField("restaurants", updated);
    },
    [restaurants, updateDetailsField]
  );

  const addRestaurant = useCallback(() => {
    updateDetailsField("restaurants", [...restaurants, { ...EMPTY_RESTAURANT }]);
  }, [restaurants, updateDetailsField]);

  const removeRestaurant = useCallback(
    (index) => {
      if (restaurants.length <= 1) return;
      updateDetailsField(
        "restaurants",
        restaurants.filter((_, i) => i !== index)
      );
    },
    [restaurants, updateDetailsField]
  );

  // --- CEP helpers ---
  const handleCepSearch = useCallback(async () => {
    const digits = detailsData.address_cep.replace(/\D/g, "");
    if (digits.length !== 8) return;

    setCepLoading(true);
    setCepError("");
    try {
      const data = await searchCep(digits);
      if (data && !data.erro) {
        updateDetailsField("address_street", data.logradouro || "");
        updateDetailsField("address_neighborhood", data.bairro || "");
        updateDetailsField("address_city", data.localidade || "");
        updateDetailsField("address_uf", data.uf || "");
      } else {
        setCepError("CEP não encontrado.");
      }
    } catch {
      setCepError("Erro ao buscar CEP.");
    } finally {
      setCepLoading(false);
    }
  }, [detailsData.address_cep, updateDetailsField]);

  const handleCepChange = (e) => {
    const value = e.target.value.replace(/\D/g, "").slice(0, 8);
    updateDetailsField("address_cep", value);
    setCepError("");
  };

  const handleCepKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleCepSearch();
    }
  };

  // --- Section heading ---
  const SectionTitle = ({ children }) => (
    <Typography
      variant="subtitle1"
      fontWeight={700}
      sx={{ mt: 1, mb: 0.5, color: "text.primary" }}
    >
      {children}
    </Typography>
  );

  return (
    <>
      {/* Thank-you header */}
      <Stack alignItems="center" spacing={1} sx={{ mb: 3 }}>
        <Box
          sx={{
            width: 48,
            height: 48,
            borderRadius: "50%",
            background: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <CheckCircleOutlineIcon sx={{ fontSize: 28, color: "#fff" }} />
        </Box>
        <Typography variant="h6" fontWeight={700} align="center" sx={{ color: "#059669" }}>
          Dados recebidos!
        </Typography>
        <Typography variant="body2" color="text.secondary" align="center" sx={{ maxWidth: 380 }}>
          Complete seus dados para agilizar o atendimento (opcional)
        </Typography>
      </Stack>

      <Divider sx={{ mb: 3 }} />

      {/* ---- Personal ---- */}
      <SectionTitle>Dados pessoais</SectionTitle>
      <Grid container spacing={2} sx={{ mb: 2.5 }}>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="E-mail"
            type="email"
            value={detailsData.email}
            onChange={(e) => updateDetailsField("email", e.target.value)}
            error={!!errors.email}
            helperText={errors.email}
            size="small"
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="CPF ou CNPJ"
            value={detailsData.cpf_cnpj}
            onChange={(e) =>
              updateDetailsField(
                "cpf_cnpj",
                e.target.value.replace(/\D/g, "").slice(0, 14)
              )
            }
            error={!!errors.cpf_cnpj}
            helperText={errors.cpf_cnpj || "Somente números"}
            size="small"
            inputProps={{ maxLength: 14 }}
          />
        </Grid>
      </Grid>

      {/* ---- Business ---- */}
      <SectionTitle>Seu negócio</SectionTitle>
      <Grid container spacing={2} sx={{ mb: 2.5 }}>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Nome do negócio / marca"
            value={detailsData.business_name}
            onChange={(e) => updateDetailsField("business_name", e.target.value)}
            size="small"
          />
        </Grid>
        <Grid item xs={12} sm={8}>
          <TextField
            fullWidth
            label="Cidade"
            value={detailsData.city}
            onChange={(e) => updateDetailsField("city", e.target.value)}
            size="small"
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField
            fullWidth
            select
            label="UF"
            value={detailsData.uf}
            onChange={(e) => updateDetailsField("uf", e.target.value)}
            error={!!errors.uf}
            helperText={errors.uf}
            size="small"
          >
            <MenuItem value="">-</MenuItem>
            {UF_OPTIONS.map((uf) => (
              <MenuItem key={uf} value={uf}>
                {uf}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
      </Grid>

      {/* ---- Restaurants / Integrations ---- */}
      <SectionTitle>Restaurantes e integrações</SectionTitle>
      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1.5 }}>
        Campos de integração são opcionais.
      </Typography>

      {errors.restaurants && (
        <Typography variant="caption" color="error" sx={{ mb: 1, display: "block" }}>
          {errors.restaurants}
        </Typography>
      )}

      <Stack spacing={2} sx={{ mb: 1.5 }}>
        {restaurants.map((restaurant, index) => {
          const rErr = restaurantErrors[index] || {};
          return (
            <Box
              key={index}
              sx={{
                p: 2,
                borderRadius: "10px",
                border: `1px solid ${alpha("#2563eb", rErr.name ? 0.4 : 0.12)}`,
                backgroundColor: alpha("#2563eb", 0.015),
                position: "relative",
              }}
            >
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{ mb: 1.5 }}
              >
                <Typography variant="subtitle2" fontWeight={600} color="text.secondary">
                  Restaurante {index + 1}
                </Typography>
                {restaurants.length > 1 && (
                  <IconButton size="small" onClick={() => removeRestaurant(index)} sx={{ color: "#dc2626" }}>
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                )}
              </Stack>

              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Nome do restaurante"
                    value={restaurant.name}
                    onChange={(e) => updateRestaurant(index, "name", e.target.value)}
                    error={!!rErr.name}
                    helperText={rErr.name}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Link ou ID do iFood (opcional)"
                    value={restaurant.ifood_link_or_id}
                    onChange={(e) => updateRestaurant(index, "ifood_link_or_id", e.target.value)}
                    size="small"
                    placeholder="https://www.ifood.com.br/delivery/..."
                  />
                </Grid>
                <Grid item xs={12}>
                  <Stack direction="row" spacing={3}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={restaurant.uses_delivery_direto}
                          onChange={(e) => updateRestaurant(index, "uses_delivery_direto", e.target.checked)}
                          size="small"
                          sx={{ "&.Mui-checked": { color: "#2563eb" } }}
                        />
                      }
                      label={<Typography variant="body2">Delivery Direto</Typography>}
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={restaurant.uses_neemo}
                          onChange={(e) => updateRestaurant(index, "uses_neemo", e.target.checked)}
                          size="small"
                          sx={{ "&.Mui-checked": { color: "#2563eb" } }}
                        />
                      }
                      label={<Typography variant="body2">Neemo</Typography>}
                    />
                  </Stack>
                </Grid>
              </Grid>
            </Box>
          );
        })}
      </Stack>

      <Button
        variant="outlined"
        startIcon={<AddIcon />}
        onClick={addRestaurant}
        fullWidth
        sx={{
          mb: 2.5,
          borderRadius: "8px",
          textTransform: "none",
          fontWeight: 600,
          borderStyle: "dashed",
          color: "#2563eb",
          borderColor: alpha("#2563eb", 0.3),
          "&:hover": {
            borderColor: "#2563eb",
            backgroundColor: alpha("#2563eb", 0.04),
            borderStyle: "dashed",
          },
        }}
      >
        Adicionar outro restaurante
      </Button>

      {/* ---- Address ---- */}
      <SectionTitle>Endereço</SectionTitle>
      <Grid container spacing={2} sx={{ mb: 2.5 }}>
        <Grid item xs={12} sm={5}>
          <TextField
            fullWidth
            label="CEP"
            value={detailsData.address_cep}
            onChange={handleCepChange}
            onKeyDown={handleCepKeyDown}
            error={!!errors.address_cep || !!cepError}
            helperText={errors.address_cep || cepError || "Digite e clique na lupa"}
            placeholder="00000000"
            size="small"
            inputProps={{ maxLength: 8 }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  {cepLoading ? (
                    <CircularProgress size={20} />
                  ) : (
                    <IconButton
                      size="small"
                      onClick={handleCepSearch}
                      disabled={detailsData.address_cep.replace(/\D/g, "").length !== 8}
                    >
                      <SearchIcon fontSize="small" />
                    </IconButton>
                  )}
                </InputAdornment>
              ),
            }}
          />
        </Grid>
        <Grid item xs={12} sm={7}>
          <TextField
            fullWidth
            label="Rua"
            value={detailsData.address_street}
            onChange={(e) => updateDetailsField("address_street", e.target.value)}
            size="small"
          />
        </Grid>
        <Grid item xs={5} sm={3}>
          <TextField
            fullWidth
            label="Número"
            value={detailsData.address_number}
            onChange={(e) => updateDetailsField("address_number", e.target.value)}
            size="small"
          />
        </Grid>
        <Grid item xs={7} sm={4}>
          <TextField
            fullWidth
            label="Complemento"
            value={detailsData.address_complement}
            onChange={(e) => updateDetailsField("address_complement", e.target.value)}
            placeholder="Apto, Sala, etc."
            size="small"
          />
        </Grid>
        <Grid item xs={12} sm={5}>
          <TextField
            fullWidth
            label="Bairro"
            value={detailsData.address_neighborhood}
            onChange={(e) => updateDetailsField("address_neighborhood", e.target.value)}
            size="small"
          />
        </Grid>
        <Grid item xs={8}>
          <TextField
            fullWidth
            label="Cidade"
            value={detailsData.address_city}
            onChange={(e) => updateDetailsField("address_city", e.target.value)}
            size="small"
          />
        </Grid>
        <Grid item xs={4}>
          <TextField
            fullWidth
            select
            label="UF"
            value={detailsData.address_uf}
            onChange={(e) => updateDetailsField("address_uf", e.target.value)}
            size="small"
          >
            <MenuItem value="">-</MenuItem>
            {UF_OPTIONS.map((uf) => (
              <MenuItem key={uf} value={uf}>
                {uf}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
      </Grid>

      {/* ---- Additional ---- */}
      <SectionTitle>Informações adicionais</SectionTitle>
      <Grid container spacing={2} sx={{ mb: 2.5 }}>
        <Grid item xs={12}>
          <TextField
            fullWidth
            select
            label="Como nos conheceu?"
            value={detailsData.how_heard}
            onChange={(e) => updateDetailsField("how_heard", e.target.value)}
            error={!!errors.how_heard}
            helperText={errors.how_heard}
            size="small"
          >
            <MenuItem value="">-</MenuItem>
            {HOW_HEARD_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Mensagem (opcional)"
            value={detailsData.message}
            onChange={(e) => updateDetailsField("message", e.target.value)}
            placeholder="Conte-nos mais sobre seu negócio ou dúvidas..."
            size="small"
          />
        </Grid>
      </Grid>

      {/* ---- LGPD ---- */}
      <SectionTitle>Termos de Uso e Política de Privacidade</SectionTitle>
      <Box
        sx={{
          maxHeight: 160,
          overflow: "auto",
          p: 2,
          borderRadius: "8px",
          border: `1px solid ${alpha("#000", 0.12)}`,
          backgroundColor: alpha("#000", 0.02),
          mb: 1.5,
          fontSize: "0.75rem",
          fontFamily: "monospace",
          lineHeight: 1.6,
          whiteSpace: "pre-wrap",
        }}
      >
        {LGPD_TEXT}
      </Box>
      <FormControlLabel
        control={
          <Checkbox
            checked={detailsData.lgpd_consent}
            onChange={(e) => updateDetailsField("lgpd_consent", e.target.checked)}
            sx={{ "&.Mui-checked": { color: "#2563eb" } }}
          />
        }
        label={
          <Typography variant="body2">
            Li e aceito os Termos de Uso e Política de Privacidade
          </Typography>
        }
      />

      {submitError && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {submitError}
        </Alert>
      )}

      {/* ---- Actions ---- */}
      <Stack spacing={1.5} sx={{ mt: 3 }}>
        <Button
          fullWidth
          variant="contained"
          endIcon={
            submitting ? (
              <CircularProgress size={18} color="inherit" />
            ) : (
              <SaveIcon />
            )
          }
          onClick={onSubmit}
          disabled={submitting}
          sx={{
            py: 1.3,
            borderRadius: "8px",
            textTransform: "none",
            fontWeight: 700,
            fontSize: "0.95rem",
            background: "linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)",
            boxShadow: "0 4px 16px rgba(37,99,235,0.3)",
            "&:hover": {
              boxShadow: "0 8px 24px rgba(37,99,235,0.4)",
            },
          }}
        >
          {submitting ? "Salvando..." : "Salvar dados adicionais"}
        </Button>
        <Link
          component="button"
          variant="body2"
          onClick={onSkip}
          underline="hover"
          sx={{
            color: "text.secondary",
            textAlign: "center",
            py: 0.5,
            fontWeight: 500,
            "&:hover": { color: "text.primary" },
          }}
        >
          Pular e finalizar
        </Link>
      </Stack>
    </>
  );
}
